import React, { useMemo, useState } from 'react';
import { useTimeTrackerStore } from '@/stores/time-tracker';
import { formatDuration, formatDecimalHours, groupEntriesByDate } from './timeTrackerUtils';
import type { TimeTrackerEntry, TimeTrackerProject } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimeEntryListProps {
  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  isViewerMode: boolean;
  currency?: string;
}

export const TimeEntryList: React.FC<TimeEntryListProps> = ({
  entries,
  projects,
  isViewerMode,
  currency = 'USD',
}) => {
  const deleteEntry = useTimeTrackerStore((s) => s.deleteEntry);
  const updateEntry = useTimeTrackerStore((s) => s.updateEntry);
  const startTimer = useTimeTrackerStore((s) => s.startTimer);
  const calculateBillableAmount = useTimeTrackerStore((s) => s.calculateBillableAmount);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Partial<TimeTrackerEntry>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const grouped = useMemo(() => groupEntriesByDate(entries), [entries]);
  const sortedDates = useMemo(
    () => [...grouped.keys()].sort((a, b) => b.localeCompare(a)),
    [grouped]
  );

  const getProject = (projectId?: string) => projects.find((p) => p.id === projectId);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((today.getTime() - d.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const toTimeInputValue = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const handleStartEdit = (entry: TimeTrackerEntry) => {
    setEditingId(entry.id);
    setEditDesc(entry.description);
  };

  const handleSaveEdit = (id: string) => {
    updateEntry(id, { description: editDesc });
    setEditingId(null);
  };

  const handleContinue = (entry: TimeTrackerEntry) => {
    startTimer({
      description: entry.description,
      projectId: entry.projectId,
      taskId: entry.taskId,
      billable: entry.billable,
      tags: entry.tags,
    });
  };

  const handleExpand = (entry: TimeTrackerEntry) => {
    if (expandedId === entry.id) {
      setExpandedId(null);
      setEditFields({});
      return;
    }
    setExpandedId(entry.id);
    setEditFields({
      description: entry.description,
      projectId: entry.projectId,
      billable: entry.billable,
      date: entry.date,
      startTime: entry.startTime,
      endTime: entry.endTime,
      duration: entry.duration,
    });
  };

  const handleSaveExpanded = (entry: TimeTrackerEntry) => {
    const updates: Partial<TimeTrackerEntry> = {};

    if (editFields.description !== undefined && editFields.description !== entry.description) {
      updates.description = editFields.description;
    }
    if (editFields.projectId !== entry.projectId) {
      updates.projectId = editFields.projectId;
    }
    if (editFields.billable !== entry.billable) {
      updates.billable = editFields.billable;
    }
    if (editFields.date && editFields.date !== entry.date) {
      updates.date = editFields.date;
    }
    if (editFields.startTime && editFields.startTime !== entry.startTime) {
      updates.startTime = editFields.startTime;
    }
    if (editFields.endTime !== undefined && editFields.endTime !== entry.endTime) {
      updates.endTime = editFields.endTime;
    }

    // Recalculate duration if times changed
    if (editFields.startTime && editFields.endTime) {
      const start = new Date(editFields.startTime);
      const end = new Date(editFields.endTime);
      if (end > start) {
        updates.duration = Math.floor((end.getTime() - start.getTime()) / 1000);
      }
    }

    if (Object.keys(updates).length > 0) {
      updateEntry(entry.id, updates);
    }
    setExpandedId(null);
    setEditFields({});
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', timeStr: string) => {
    const dateStr = editFields.date || new Date().toISOString().slice(0, 10);
    const iso = `${dateStr}T${timeStr}:00.000Z`;
    setEditFields((prev) => ({ ...prev, [field]: iso }));
  };

  if (entries.length === 0) {
    return (
      <div data-testid="time-entry-list" style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 14 }}>
        No time entries yet. Start the timer or add a manual entry.
      </div>
    );
  }

  return (
    <div data-testid="time-entry-list" style={{ flex: 1, overflow: 'auto' }}>
      {sortedDates.map((date) => {
        const dayEntries = grouped.get(date) || [];
        const dayTotal = dayEntries.reduce((acc, e) => acc + e.duration, 0);

        return (
          <div key={date} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}` }}>
            {/* Day header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', background: pmTheme.surfaceSubtle, borderBottom: `1px solid ${pmTheme.border}` }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: pmTheme.text }}>{formatDate(date)}</span>
              <span style={{ fontSize: 13, color: pmTheme.textMuted, fontFamily: 'monospace' }}>{formatDecimalHours(dayTotal)}</span>
            </div>

            {/* Entries */}
            {dayEntries.map((entry) => {
              const project = getProject(entry.projectId);
              const amount = calculateBillableAmount(entry);
              const isEditing = editingId === entry.id;
              const isExpanded = expandedId === entry.id;

              return (
                <div key={entry.id}>
                  {/* Entry row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 16px',
                      borderBottom: isExpanded ? 'none' : `1px solid ${pmTheme.borderSubtle}`,
                      cursor: isViewerMode ? 'default' : 'pointer',
                      transition: 'background 0.15s',
                      background: isExpanded ? pmTheme.successSoft : 'transparent',
                    }}
                    onClick={() => !isViewerMode && !isEditing && handleExpand(entry)}
                    onMouseEnter={(e) => { if (!isViewerMode) e.currentTarget.style.background = isExpanded ? pmTheme.successSoft : pmTheme.surfaceHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isExpanded ? pmTheme.successSoft : 'transparent'; }}
                  >
                    {/* Project color dot */}
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: project?.color || pmTheme.input, flexShrink: 0 }} />

                    {/* Description */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          onBlur={() => handleSaveEdit(entry.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(entry.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '100%', padding: '2px 6px', border: `1px solid ${pmTheme.primary}`, borderRadius: 4, fontSize: 13, outline: 'none', color: pmTheme.text, background: pmTheme.surface }}
                        />
                      ) : (
                        <span style={{ fontSize: 13, color: pmTheme.text }}>
                          {entry.description || '(no description)'}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                        {project && <span style={{ fontSize: 11, color: project.color, fontWeight: 500 }}>{project.name}</span>}
                        {entry.tags.map((tag) => (
                          <span key={tag} style={{ fontSize: 10, color: pmTheme.textSubtle, background: pmTheme.surfaceMuted, padding: '1px 6px', borderRadius: 10 }}>{tag}</span>
                        ))}
                      </div>
                    </div>

                    {/* Billable indicator */}
                    {entry.billable && (
                      <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }} title={`${currency} ${amount.toFixed(2)}`}>$</span>
                    )}

                    {/* Time range */}
                    <span style={{ fontSize: 12, color: pmTheme.textSubtle, whiteSpace: 'nowrap' }}>
                      {formatTime(entry.startTime)}{entry.endTime ? ` - ${formatTime(entry.endTime)}` : ''}
                    </span>

                    {/* Duration */}
                    <span style={{ fontSize: 13, fontFamily: 'monospace', color: pmTheme.text, minWidth: 64, textAlign: 'right' }}>
                      {formatDuration(entry.duration)}
                    </span>

                    {/* Continue action */}
                    {!isViewerMode && (
                      <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleContinue(entry)}
                          title="Continue this entry"
                          style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${pmTheme.input}`, background: pmTheme.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#10b981' }}
                        >
                          ▶
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded edit form */}
                  {isExpanded && !isViewerMode && (
                    <div
                      style={{
                        padding: '10px 16px 12px 36px',
                        background: pmTheme.successSoft,
                        borderBottom: `1px solid ${pmTheme.successBorder}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Description */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 11, color: pmTheme.textMuted, width: 70, flexShrink: 0 }}>Description</label>
                        <input
                          type="text"
                          value={editFields.description ?? ''}
                          onChange={(e) => setEditFields((prev) => ({ ...prev, description: e.target.value }))}
                          style={{ flex: 1, padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, outline: 'none', color: pmTheme.text, background: pmTheme.surface }}
                        />
                      </div>

                      {/* Project + Billable row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 11, color: pmTheme.textMuted, width: 70, flexShrink: 0 }}>Project</label>
                        <select
                          value={editFields.projectId || ''}
                          onChange={(e) => setEditFields((prev) => ({ ...prev, projectId: e.target.value || undefined }))}
                          style={{ flex: 1, padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }}
                        >
                          <option value="">No project</option>
                          {projects.filter((p) => !p.archived).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: pmTheme.textMuted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={editFields.billable ?? false}
                            onChange={(e) => setEditFields((prev) => ({ ...prev, billable: e.target.checked }))}
                          />
                          Billable
                        </label>
                      </div>

                      {/* Date + Times row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 11, color: pmTheme.textMuted, width: 70, flexShrink: 0 }}>Date</label>
                        <input
                          type="date"
                          value={editFields.date || ''}
                          onChange={(e) => setEditFields((prev) => ({ ...prev, date: e.target.value }))}
                          style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }}
                        />
                        <label style={{ fontSize: 11, color: pmTheme.textMuted, flexShrink: 0 }}>Start</label>
                        <input
                          type="time"
                          value={editFields.startTime ? toTimeInputValue(editFields.startTime) : ''}
                          onChange={(e) => handleTimeChange('startTime', e.target.value)}
                          style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }}
                        />
                        <label style={{ fontSize: 11, color: pmTheme.textMuted, flexShrink: 0 }}>End</label>
                        <input
                          type="time"
                          value={editFields.endTime ? toTimeInputValue(editFields.endTime) : ''}
                          onChange={(e) => handleTimeChange('endTime', e.target.value)}
                          style={{ padding: '4px 8px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 13, color: pmTheme.text, background: pmTheme.surface }}
                        />
                      </div>

                      {/* Save/Cancel/Delete buttons */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center' }}>
                        <button
                          onClick={() => handleSaveExpanded(entry)}
                          style={{ padding: '5px 16px', background: '#10b981', color: pmTheme.textInverse, border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setExpandedId(null); setEditFields({}); setConfirmDeleteId(null); }}
                          style={{ padding: '5px 16px', background: 'transparent', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: pmTheme.textMuted }}
                        >
                          Cancel
                        </button>
                        <div style={{ marginLeft: 'auto' }}>
                          {confirmDeleteId === entry.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, color: pmTheme.danger }}>Delete this entry?</span>
                              <button
                                onClick={() => { deleteEntry(entry.id); setExpandedId(null); setEditFields({}); setConfirmDeleteId(null); }}
                                style={{ padding: '4px 12px', background: pmTheme.danger, color: pmTheme.textInverse, border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                              >
                                Yes, delete
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                style={{ padding: '4px 12px', background: 'transparent', border: `1px solid ${pmTheme.input}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: pmTheme.textMuted }}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(entry.id)}
                              style={{ padding: '5px 14px', background: 'transparent', border: `1px solid ${pmTheme.dangerBorder}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: pmTheme.danger }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

TimeEntryList.displayName = 'TimeEntryList';
